import { useEffect, useState } from 'react';
import { getLocationManagers } from '../../components/location/requests/location-requests';
import { getErrorMessage } from '../../util/NetworkErrorUtil';
import { LocationManagerData } from '../../views/create-location-wizard/types';

/**
 * Module-level cache of fetched manager lists keyed by account id.
 *
 * The Create/Edit Location wizard keeps step components mounted (inactive ones
 * are hidden), and this hook may also be re-mounted if the supplier leaves and
 * returns to the wizard. Caching here avoids redundant network calls and
 * prevents a visible loading flash when the hook is used again.
 */
const managersCache = new Map<string, LocationManagerData[]>();

/**
 * Managers created from the wizard, keyed by account id then username. Kept apart from the cache so
 * they survive a failed list load and are merged into any list that loads afterwards.
 */
const createdManagers = new Map<string, Map<string, LocationManagerData>>();

const mergeCreated = (accountId: string, list: LocationManagerData[]): LocationManagerData[] => {
  const created = createdManagers.get(accountId);
  if (!created || created.size === 0) return list;
  return [...list.filter((m) => !created.has(m.username)), ...created.values()];
};

/** In-flight requests keyed by account id so concurrent mounts share one fetch. */
const inFlight = new Map<string, Promise<LocationManagerData[]>>();

/**
 * Drops the cached manager list so the next {@link useLocationManagers} mount
 * refetches. The wizard calls this on open so the dropdown — and the phone that
 * gets copied onto the selected manager — reflect any Manage Users edits made
 * since the list was last loaded. Omit `accountId` to clear every account.
 */
export function invalidateLocationManagers(accountId?: string): void {
  if (accountId) {
    managersCache.delete(accountId);
    inFlight.delete(accountId);
    createdManagers.delete(accountId);
  } else {
    managersCache.clear();
    inFlight.clear();
    createdManagers.clear();
  }
}

/** Mounted hooks, notified when the cached list for their account changes underneath them. */
const cacheListeners = new Set<(accountId: string) => void>();

const putInCache = (accountId: string, cached: LocationManagerData[], manager: LocationManagerData): void => {
  managersCache.set(accountId, [...cached.filter((m) => m.username !== manager.username), manager]);
  cacheListeners.forEach((listener) => listener(accountId));
};

/**
 * Adds (or replaces) a manager and refreshes every mounted dropdown, so a user created from the
 * wizard shows up without a refetch. The manager is also remembered and merged into any list that
 * loads later. When the list is still loading, the manager is added once it arrives — that response
 * may predate the new user. If the list failed to load, the dropdown shows the created managers
 * only and the partial list is never cached as if it were complete.
 */
export function upsertLocationManager(accountId: string, manager: LocationManagerData): void {
  const created = createdManagers.get(accountId) ?? new Map<string, LocationManagerData>();
  created.set(manager.username, manager);
  createdManagers.set(accountId, created);

  const cached = managersCache.get(accountId);
  if (cached) {
    putInCache(accountId, cached, manager);
    return;
  }
  const pending = inFlight.get(accountId);
  if (pending) {
    pending.then(
      (loaded) => putInCache(accountId, managersCache.get(accountId) ?? loaded, manager),
      () => cacheListeners.forEach((listener) => listener(accountId))
    );
    return;
  }
  cacheListeners.forEach((listener) => listener(accountId));
}

interface UseLocationManagersResult {
  managers: LocationManagerData[];
  loading: boolean;
  error: string | null;
}

/**
 * Loads the list of users eligible to be a location manager for an account.
 *
 * Results are cached for the lifetime of the page so navigating back and forth
 * between wizard steps does not re-fetch or flash a loading state.
 *
 * @param accountId The supplier account id (no fetch occurs when undefined)
 */
export function useLocationManagers(accountId: string | undefined): UseLocationManagersResult {
  const cached = accountId ? managersCache.get(accountId) : undefined;

  const [managers, setManagers] = useState<LocationManagerData[]>(cached || []);
  // Only show a loading state when we have nothing cached to render yet.
  const [loading, setLoading] = useState<boolean>(Boolean(accountId) && !cached);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!accountId) return undefined;
    const listener = (changedAccountId: string) => {
      if (changedAccountId !== accountId) return;
      setManagers(managersCache.get(changedAccountId) ?? mergeCreated(changedAccountId, []));
    };
    cacheListeners.add(listener);
    return () => {
      cacheListeners.delete(listener);
    };
  }, [accountId]);

  useEffect(() => {
    if (!accountId) {
      setLoading(false);
      return;
    }

    // Serve cached data immediately, skipping the network round-trip.
    const existing = managersCache.get(accountId);
    if (existing) {
      setManagers(existing);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    // Reuse an in-flight request if another mount already started one.
    const request =
      inFlight.get(accountId) ||
      getLocationManagers(accountId)
        .then((res: { text: string }) => {
          const parsed: LocationManagerData[] = mergeCreated(accountId, JSON.parse(res.text));
          managersCache.set(accountId, parsed);
          return parsed;
        })
        .finally(() => {
          inFlight.delete(accountId);
        });

    inFlight.set(accountId, request);

    request
      .then((parsed) => {
        if (!cancelled) setManagers(parsed);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setManagers(mergeCreated(accountId, []));
          setError(getErrorMessage(err, 'Failed to load location managers. Please try again.'));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [accountId]);

  return { managers, loading, error };
}

